import _ from "lodash";

import type {
    ContactRequestType,
    TherapeuticArea,
    Tier,
} from "components/Admin/Organizations/types/Tier.type";

export type DiffableTierField =
    | "category"
    | "subCategory"
    | "code"
    | "administrativeContacts"
    | "escalationContacts"
    | "managerContacts"
    | "hierarchyCodes"
    | "therapeuticAreas";

export interface TierPropertyChange {
    field: DiffableTierField;
    label: string;
    before: unknown;
    after: unknown;
}

const FIELD_LABELS: Record<DiffableTierField, string> = {
    category: "Category",
    subCategory: "Sub Category",
    code: "Code",
    administrativeContacts: "Administrative Contacts",
    escalationContacts: "Escalation Contacts",
    managerContacts: "Manager Contacts",
    hierarchyCodes: "Hierarchy Codes",
    therapeuticAreas: "Therapeutic Area",
};

const CONTACT_FIELDS: DiffableTierField[] = [
    "administrativeContacts",
    "escalationContacts",
    "managerContacts",
];

function normalizeContactList(value: ContactRequestType[] | undefined) {
    return _.sortBy(value ?? [], (c) => c.userId).map((c) => c.userId);
}

function normalizeStringList(value: string[] | undefined) {
    return _.sortBy(value ?? []);
}

function normalizeTherapeuticAreas(value: TherapeuticArea[] | undefined) {
    return _.sortBy(value ?? [], (area) => area.id).map((area) => area.id);
}

function valuesEqual(field: DiffableTierField, a: unknown, b: unknown) {
    if (CONTACT_FIELDS.includes(field)) {
        return _.isEqual(
            normalizeContactList(a as ContactRequestType[]),
            normalizeContactList(b as ContactRequestType[]),
        );
    }
    if (field === "hierarchyCodes") {
        return _.isEqual(
            normalizeStringList(a as string[]),
            normalizeStringList(b as string[]),
        );
    }
    if (field === "therapeuticAreas") {
        return _.isEqual(
            normalizeTherapeuticAreas(a as TherapeuticArea[]),
            normalizeTherapeuticAreas(b as TherapeuticArea[]),
        );
    }
    return (a ?? "") === (b ?? "");
}

export function formatTierFieldValue(
    field: DiffableTierField,
    value: unknown,
): string {
    if (value == null) return "—";
    if (CONTACT_FIELDS.includes(field)) {
        const list = value as ContactRequestType[];
        return list.length === 0
            ? "—"
            : list.map((c) => c.userName).join(", ");
    }
    if (field === "hierarchyCodes") {
        const list = value as string[];
        return list.length === 0 ? "—" : list.join(", ");
    }
    if (field === "therapeuticAreas") {
        const list = value as TherapeuticArea[];
        return list.length === 0 ? "—" : list.map((area) => area.name).join(", ");
    }
    const str = String(value);
    return str.length === 0 ? "—" : str;
}

export function diffTierProperties(
    prev: Tier,
    next: Tier,
): TierPropertyChange[] {
    const fields = Object.keys(FIELD_LABELS) as DiffableTierField[];
    const changes: TierPropertyChange[] = [];

    for (const field of fields) {
        const before = prev[field];
        const after = next[field];

        if (!valuesEqual(field, before, after)) {
            changes.push({
                field,
                label: FIELD_LABELS[field],
                before,
                after,
            });
        }
    }

    return changes;
}
